import {
  EntityId,
  EntitySelectEntity,
  EntitySelectSubEntity,
} from '@factorialco/f0-react/dist/experimental'
import { FinancialWorkspaceValueObjectsAgreementDataTypeEnum } from 'generated/resources/datatable.graphql'

import { EntityGroup } from 'modules/core/components/EntitySelectWrapper/types'
import i18n from 'modules/core/lib/i18n'

import { Availabilities } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/Availabilities'
import { FWPermissions } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/usePermissions'

import { Data } from '../hooks/useData'

export const GROUP_VALUES = {
  ALL: 'all',
}

export type EntityType = 'agreement_data' | 'employee_expenses' | 'labor_costs'

export const GROUPS: EntityGroup[] = [
  {
    value: GROUP_VALUES.ALL,
    label: 'All',
  },
]

export const DATA_SOURCE_GROUPS = {
  agreement_data: '',
  employee_expenses: '',
  labor_costs: '',
}

interface Entity {
  expandedElements?: EntityId[]
  availabilities: Availabilities
  permissions: FWPermissions
  subItems?: Record<EntityType, EntitySelectSubEntity[]>
}

export const ENTITIES = (config: Entity) => {
  return {
    AGREEMENT_DATA_ENTITY: {
      id: 'agreement_data',
      name: i18n.t(
        'finance.financial_workspace.employee_costs.selector.groups.agreement_cost.label'
      ),
      enabled: true,
      subItems: config.subItems?.agreement_data ?? [],
      expanded: config.expandedElements?.includes('agreement_data'),
    },
    EMPLOYEE_EXPENSES_ENTITY: {
      id: 'employee_expenses',
      name: i18n.t(
        'finance.financial_workspace.employee_costs.selector.groups.employee_expenses.label'
      ),
      enabled: config.availabilities.modules.expenses && config.permissions.canSeeExpenses,
      subItems: config.subItems?.employee_expenses ?? [],
      expanded: config.expandedElements?.includes('employee_expenses'),
    },
    LABOR_COSTS_ENTITY: {
      id: 'labor_costs',
      name: i18n.t('finance.financial_workspace.employee_costs.selector.groups.labor_costs.label'),
      enabled: !config.availabilities.seePayrollLanding && config.availabilities.canSeeLaborCosts,
      subItems: config.subItems?.labor_costs ?? [],
      expanded: config.expandedElements?.includes('labor_costs'),
    },
  }
}

export const DATA_SOURCE_GROUPS_KEYS = Object.keys(DATA_SOURCE_GROUPS)

export type DATA_SOURCE_DATA = {
  labor_costs: string[]
  employee_expenses: string[]
  agreement_data: FinancialWorkspaceValueObjectsAgreementDataTypeEnum[]
}

export function dataSourceGroups(
  expandedElements: EntityId[],
  data: Data,
  availabilities: Availabilities,
  permissions: FWPermissions
): EntitySelectEntity[] {
  const { expensesCategories, agreementDataSubItems, payrollCategory } = data

  const entities = ENTITIES({
    expandedElements,
    availabilities,
    permissions,
    subItems: {
      agreement_data: agreementDataSubItems.map(({ id: subId, label: subName }) => ({
        subId,
        subName,
      })),
      employee_expenses: expensesCategories.map((category) => ({
        subId: category.id,
        subName: category.label,
      })),
      labor_costs: [
        {
          subId: `labor_costs_${payrollCategory.id}`,
          subName: payrollCategory.label,
        },
      ],
    },
  })

  return Object.values(entities).filter((entity) => entity.enabled)
}
